import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { IoMdArrowRoundBack } from 'react-icons/io';
import ServiceCard from './AmenityServiceCard';
import BookingModal from './AmenityBooking';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import Cookies from 'js-cookie';
import { IoClose } from 'react-icons/io5';

const BQ_BASE_URL = process.env.REACT_APP_BQ_BASE_URL;
const REACT_APP_RAZORPAY_KEY = process.env.REACT_APP_RAZORPAY_KEY;

// Guest data functions
const getGuestData = async () => {
  const accessToken = Cookies.get('access_token');
  if (!accessToken) return null;

  try {
    const guestId = Cookies.get('guest_id') || getGuestIdFromToken(accessToken);
    if (!guestId) return null;

    const profileResponse = await fetch(`${BQ_BASE_URL}/bq/api/profile?guest_id=${guestId}`, {
      method: 'GET',
      headers: {
        Accept: 'application/json',
        Authorization: `Bearer ${accessToken}`,
      },
    });

    if (!profileResponse.ok) {
      throw new Error(`Profile API Error: ${profileResponse.statusText}`);
    }

    const profileData = await profileResponse.json();

    // Check if this is a corporate guest
    const isCorporateGuest = profileData.companyid && profileData.companyname;

    const isDecrypted = profileData.emailid && profileData.emailid.length < 50;

    if (isDecrypted) {
      return {
        first_name: profileData.firstname || '',
        last_name: profileData.lastname || '',
        email: profileData.emailid || '',
        phone_number: profileData.phonenumber || '',
        country_code: '+91',
        client_type: profileData.clienttype || 'Leisure',
        company_id: profileData.companyid || '',
        company_name: profileData.companyname || '',
        is_corporate: isCorporateGuest,
      };
    }

    const decryptedFields = await decryptUserData({
      email: profileData.emailid,
      phone_number: profileData.phonenumber,
      country_code: profileData.countrycode,
    });

    return {
      first_name: profileData.firstname || '',
      last_name: profileData.lastname || '',
      email: decryptedFields.email || profileData.emailid || '',
      phone_number: decryptedFields.phone_number || profileData.phonenumber || '',
      country_code: '+91',
      client_type: profileData.clienttype || 'Leisure',
      company_id: profileData.companyid || '',
      company_name: profileData.companyname || '',
      is_corporate: isCorporateGuest,
    };
  } catch (error) {
    console.error('Error fetching guest data:', error);
    return null;
  }
};

const getGuestIdFromToken = (token) => {
  try {
    const payload = JSON.parse(atob(token.split('.')[1]));
    return payload.sub;
  } catch (e) {
    console.error('Error parsing token:', e);
    return null;
  }
};

const decryptUserData = async (encryptedData) => {
  try {
    if (encryptedData.email && encryptedData.email.length < 50) {
      return encryptedData;
    }

    const accessToken = Cookies.get('access_token');
    if (!accessToken) {
      throw new Error('No access token found');
    }

    const response = await fetch(`${BQ_BASE_URL}/bq/api/decrypt-fields`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${accessToken}`,
      },
      body: JSON.stringify({
        encrypted_fields: {
          email: encryptedData.email,
          phone_number: encryptedData.phone_number,
          country_code: encryptedData.country_code,
        },
      }),
    });

    if (response.status === 401) {
      return encryptedData;
    }

    if (!response.ok) {
      throw new Error(`Decryption failed: ${response.statusText}`);
    }

    return await response.json();
  } catch (error) {
    console.error('Decryption error:', error);
    return encryptedData;
  }
};

const AmenityServices = () => {
  const [services, setServices] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedService, setSelectedService] = useState(null);
  const [formData, setFormData] = useState({
    serviceid: '',
    itemid: '',
    urgencylevel: 'Normal',
    scheduledDate: new Date().toISOString().split('T')[0],
    scheduledTime: new Date().toTimeString().slice(0, 5),
    scheduledtime: `${new Date().toISOString().split('T')[0]}T${new Date()
      .toTimeString()
      .slice(0, 5)}:00`,
    quantity: '',
    specialinstructions: '',
  });
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [razorpayLoaded, setRazorpayLoaded] = useState(false);
  const [guestData, setGuestData] = useState(null);
  const [isCorporateGuest, setIsCorporateGuest] = useState(false);

  const bookingDetails = JSON.parse(localStorage.getItem('bookingDetails')) || {};
  // Extract booking ID from nested structure
  const bookingId =
    bookingDetails?.primaryBooking?.booking_id ||
    bookingDetails?.bookings?.[0]?.booking_id ||
    bookingDetails?.bookingid;

  // Fetch room services from API
  useEffect(() => {
    const fetchServices = async () => {
      try {
        const response = await fetch(`${BQ_BASE_URL}/bq/api/amenitycategories/`, {
          headers: { Accept: 'application/json' },
        });
        const data = await response.json();
        setServices(data);
      } catch (error) {
        console.error('Error fetching room services:', error);
      }
    };

    fetchServices();
  }, []);

  // Fetch guest data on component mount
  useEffect(() => {
    const fetchGuestData = async () => {
      const guestInfo = await getGuestData();
      setGuestData(guestInfo);
      setIsCorporateGuest(guestInfo?.is_corporate || false);
    };

    fetchGuestData();
  }, []);

  const handleBookClick = (service) => {
    setSelectedService({
      ...service,
      category_name: service.name,
    });
    setFormData((prev) => ({
      ...prev,
      serviceName: service.name,
      scheduledDate: new Date().toISOString().split('T')[0],
      scheduledTime: new Date().toTimeString().slice(0, 5),
      scheduledtime: `${new Date().toISOString().split('T')[0]}T${new Date()
        .toTimeString()
        .slice(0, 5)}:00`,
    }));
    setIsModalOpen(true);
  };

  const confirmServiceBooking = async () => {
    try {
      if (!bookingId) {
        throw new Error('Booking ID not found. Please try again.');
      }

      // Construct URL with query parameters
      const url = new URL(`${BQ_BASE_URL}/bq/api/create_amenity_service_billing`);
      url.searchParams.append('bookingid', bookingId);
      url.searchParams.append('category_name', selectedService.category_name);

      const response = await fetch(url.toString(), {
        method: 'POST',
        headers: {
          Accept: 'application/json',
        },
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to confirm service');
      }

      const data = await response.json();

      // Store billing details & selected service
      localStorage.setItem('billingDetails', JSON.stringify(data));
      localStorage.setItem('selectedService', JSON.stringify(selectedService));

      toast.success('Service confirmed successfully');
      closeConfirmationModal();
      setShowPaymentModal(true);
    } catch (error) {
      toast.error(error.message || 'An error occurred while confirming the service.');
      console.error('Booking error:', error);
    }
  };

  const closeConfirmationModal = () => {
    setSelectedService(null);
    setIsModalOpen(false);
  };

  useEffect(() => {
    const loadRazorpayScript = () => {
      const script = document.createElement('script');
      script.src = 'https://checkout.razorpay.com/v1/checkout.js';
      script.async = true;
      script.onload = () => setRazorpayLoaded(true);
      script.onerror = () => {
        toast.error('Error loading Razorpay script. Please try again.');
      };
      document.body.appendChild(script);
    };

    loadRazorpayScript();
  }, []);

  const handlePaymentOption = async (paymentMethod) => {
    if (!razorpayLoaded) {
      toast.error('Razorpay script is not loaded yet.');
      return;
    }

    if (paymentMethod === 'payNow') {
      try {
        // Retrieve selected service from localStorage
        const selectedService = JSON.parse(localStorage.getItem('selectedService'));
        if (!selectedService) {
          toast.error('No service selected for payment.');
          return;
        }

        // Retrieve billing details from localStorage
        const billingDetails = JSON.parse(localStorage.getItem('billingDetails'));
        if (!billingDetails || !billingDetails.billingid) {
          toast.error('Billing details not found. Please try again.');
          return;
        }

        const billingId = billingDetails.billingid;

        const apiUrl = `${BQ_BASE_URL}/bq/api/razorpay/create_payment_order?billingid=${billingId}&bookingid=${bookingId}`;

        const response = await fetch(apiUrl, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
          },
        });

        if (!response.ok) throw new Error('Failed to create payment order');

        const orderData = await response.json();

        if (orderData && orderData.razorpay_order_id) {
          const options = {
            key: REACT_APP_RAZORPAY_KEY,
            amount: orderData.amount,
            currency: 'INR',
            name: 'Pagoda Hotel',
            description: selectedService.name,
            order_id: orderData.razorpay_order_id,
            handler: async function (response) {
              const paymentDetails = {
                razorpay_order_id: response.razorpay_order_id,
                razorpay_payment_id: response.razorpay_payment_id,
                razorpay_signature: response.razorpay_signature,
              };

              try {
                const verificationResponse = await fetch(
                  `${BQ_BASE_URL}/bq/api/razorpay/verify_payment`,
                  {
                    method: 'POST',
                    headers: {
                      'Content-Type': 'application/json',
                      Accept: 'application/json',
                    },
                    body: JSON.stringify(paymentDetails),
                  }
                );

                if (verificationResponse.ok) {
                  toast.success('Payment successful and verified!');
                  window.location.href = '/amenities-payment-succesful';
                } else {
                  toast.error('Payment verification failed.');
                }
              } catch (error) {
                console.error('Verification error:', error);
                toast.error('Error verifying payment. Please try again.');
              }
            },
            prefill: {
              name: `${bookingDetails.primaryBooking?.guest_name || bookingDetails.primary_guest}`,
              email: bookingDetails.primaryBooking?.guest_email,
              contact: bookingDetails.primaryBooking?.guest_phone?.toString(),
            },
            theme: {
              color: '#3399cc',
            },
          };

          const rzp = new window.Razorpay(options);
          rzp.open();
        }
      } catch (error) {
        console.error('Payment error:', error);
        toast.error('Error processing payment. Please try again.');
      }
    } else if (paymentMethod === 'payLater') {
      toast.info('Payment deferred. Please pay at the front desk.');
      localStorage.removeItem('selectedService');
      localStorage.removeItem('billingDetails');
      setShowPaymentModal(false);
    } else if (paymentMethod === 'billToCompany') {
      toast.info(
        `Bill to company option selected. Charges will be billed to ${
          guestData?.company_name || 'your company'
        }.`
      );
      localStorage.removeItem('selectedService');
      localStorage.removeItem('billingDetails');
      setShowPaymentModal(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 py-8 px-4 sm:px-6 lg:px-8">
      <Link
        to="/reservation/options/booking/welcome"
        className="absolute top-12 left-8 text-black hover:text-gray-800 transition font-semibold text-2xl"
      >
        <IoMdArrowRoundBack size={32} />
      </Link>

      <div className="px-10 mt-10 mx-auto">
        <div className="text-center mb-12">
          <h1 className="text-3xl font-poppins font-bold text-gray-900 mb-2">Amenity Services</h1>
          <h2 className="text-lg font-poppins font-semibold text-center mb-1 text-black">
            From essential conveniences to premium comforts, our amenity services let you customize
            your stay to perfectly suit your needs
          </h2>
          <hr className="border border-gray-300 w-1/4 mx-auto mb-4" />
        </div>

        <div className="grid grid-cols-1 font-poppins gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {services.map((service) => (
            <ServiceCard key={service.id} service={service} onBookClick={handleBookClick} />
          ))}
        </div>
      </div>

      {isModalOpen && (
        <BookingModal
          formData={formData}
          setFormData={setFormData}
          selectedService={selectedService}
          onClose={() => setIsModalOpen(false)}
          confirmServiceBooking={confirmServiceBooking}
        />
      )}
      {showPaymentModal && (
        <div className="fixed inset-0 flex justify-center items-center bg-gray-800 bg-opacity-70 z-50">
          <div className="bg-white p-6 rounded-lg w-4/12 text-center shadow-md transform transition-all duration-200">
            <button
              onClick={() => {
                setShowPaymentModal(false);
                localStorage.removeItem('selectedService');
                localStorage.removeItem('billingDetails');
              }}
              className="absolute top-3 right-3  text-gray-400 hover:text-gray-600 transition-colors duration-200"
            >
              <IoClose size={24} />
            </button>
            <h3 className="text-2xl font-semibold mt-4 mb-4 text-gray-900">
              Complete Your Payment
            </h3>
            <p className="text-md text-gray-600 mb-6">
              Your booking has been successfully confirmed! Please select a payment method to
              proceed:
            </p>

            <div className="flex justify-center gap-4">
              {/* Pay Later option for leisure guests */}
              {!isCorporateGuest && (
                <button
                  onClick={() => handlePaymentOption('payLater')}
                  className="bg-black text-white px-10 py-2 rounded-md hover:text-gray-200 transition-all duration-200 font-semibold"
                >
                  Pay Later
                </button>
              )}

              {/* Bill to Company option for corporate guests */}
              {isCorporateGuest && (
                <button
                  onClick={() => handlePaymentOption('billToCompany')}
                  className="bg-black text-white px-10 py-2 rounded-md hover:text-gray-200 transition-all duration-200 font-semibold"
                >
                  Bill to Company
                </button>
              )}

              {/* Pay Now option for all guests */}
              <button
                onClick={() => handlePaymentOption('payNow')}
                className="bg-green-600 text-white px-10 py-2 rounded-md hover:text-black transition-all duration-200 font-semibold"
              >
                Pay Now
              </button>
            </div>
          </div>
        </div>
      )}
      <ToastContainer />
    </div>
  );
};

export default AmenityServices;

// old code
// import { useState, useEffect } from "react";
// import { Link } from "react-router-dom";
// import { IoMdArrowRoundBack } from "react-icons/io";
// import ServiceCard from "./AmenityServiceCard";
// import BookingModal from "./AmenityBooking";
// import { toast, ToastContainer } from "react-toastify";
// import "react-toastify/dist/ReactToastify.css";
// import Cookies from "js-cookie";

// const BQ_BASE_URL = process.env.REACT_APP_BQ_BASE_URL;

// // Guest data functions
// const getGuestData = async () => {
//   const accessToken = Cookies.get("access_token");
//   if (!accessToken) return null;

//   try {
//     const guestId = Cookies.get("guest_id") || getGuestIdFromToken(accessToken);
//     if (!guestId) return null;

//     const profileResponse = await fetch(
//       `${BQ_BASE_URL}/bq/api/profile?guest_id=${guestId}`,
//       {
//         method: "GET",
//         headers: {
//           Accept: "application/json",
//           Authorization: `Bearer ${accessToken}`,
//         },
//       }
//     );

//     if (!profileResponse.ok) {
//       throw new Error(`Profile API Error: ${profileResponse.statusText}`);
//     }

//     const profileData = await profileResponse.json();

//     // Check if this is a corporate guest
//     const isCorporateGuest = profileData.companyid && profileData.companyname;

//     const isDecrypted = profileData.emailid && profileData.emailid.length < 50;

//     if (isDecrypted) {
//       return {
//         first_name: profileData.firstname || "",
//         last_name: profileData.lastname || "",
//         email: profileData.emailid || "",
//         phone_number: profileData.phonenumber || "",
//         country_code: "+91",
//         client_type: profileData.clienttype || "Leisure",
//         company_id: profileData.companyid || "",
//         company_name: profileData.companyname || "",
//         is_corporate: isCorporateGuest,
//       };
//     }

//     const decryptedFields = await decryptUserData({
//       email: profileData.emailid,
//       phone_number: profileData.phonenumber,
//       country_code: profileData.countrycode,
//     });

//     return {
//       first_name: profileData.firstname || "",
//       last_name: profileData.lastname || "",
//       email: decryptedFields.email || profileData.emailid || "",
//       phone_number:
//         decryptedFields.phone_number || profileData.phonenumber || "",
//       country_code: "+91",
//       client_type: profileData.clienttype || "Leisure",
//       company_id: profileData.companyid || "",
//       company_name: profileData.companyname || "",
//       is_corporate: isCorporateGuest,
//     };
//   } catch (error) {
//     console.error("Error fetching guest data:", error);
//     return null;
//   }
// };

// const getGuestIdFromToken = (token) => {
//   try {
//     const payload = JSON.parse(atob(token.split(".")[1]));
//     return payload.sub;
//   } catch (e) {
//     console.error("Error parsing token:", e);
//     return null;
//   }
// };

// const decryptUserData = async (encryptedData) => {
//   try {
//     if (encryptedData.email && encryptedData.email.length < 50) {
//       return encryptedData;
//     }

//     const accessToken = Cookies.get("access_token");
//     if (!accessToken) {
//       throw new Error("No access token found");
//     }

//     const response = await fetch(`${BQ_BASE_URL}/bq/api/decrypt-fields`, {
//       method: "POST",
//       headers: {
//         "Content-Type": "application/json",
//         Authorization: `Bearer ${accessToken}`,
//       },
//       body: JSON.stringify({
//         encrypted_fields: {
//           email: encryptedData.email,
//           phone_number: encryptedData.phone_number,
//           country_code: encryptedData.country_code,
//         },
//       }),
//     });

//     if (response.status === 401) {
//       return encryptedData;
//     }

//     if (!response.ok) {
//       throw new Error(`Decryption failed: ${response.statusText}`);
//     }

//     return await response.json();
//   } catch (error) {
//     console.error("Decryption error:", error);
//     return encryptedData;
//   }
// };

// const AmenityServices = () => {
//   const [services, setServices] = useState([]);
//   const [isModalOpen, setIsModalOpen] = useState(false);
//   const [selectedService, setSelectedService] = useState(null);
//   const [formData, setFormData] = useState({
//     serviceName: "",
//     itemName: "",
//     urgencyLevel: "Normal",
//     scheduleTime: new Date().toISOString(), // Set current datetime as default
//     requestedTime: new Date().toISOString().slice(0, 16),
//     specialInstruction: "",
//   });
//   const [showPaymentModal, setShowPaymentModal] = useState(false);
//   const [razorpayLoaded, setRazorpayLoaded] = useState(false);
//   const [guestData, setGuestData] = useState(null);
//   const [isCorporateGuest, setIsCorporateGuest] = useState(false);

//   const bookingDetails =
//     JSON.parse(localStorage.getItem("bookingDetails")) || {};
//   // Extract booking ID from nested structure
//   const bookingId =
//     bookingDetails?.primaryBooking?.booking_id ||
//     bookingDetails?.bookings?.[0]?.booking_id ||
//     bookingDetails?.bookingid;

//   // Fetch room services from API
//   useEffect(() => {
//     const fetchServices = async () => {
//       try {
//         const response = await fetch(
//           `${BQ_BASE_URL}/bq/api/amenitycategories/`,
//           { headers: { Accept: "application/json" } }
//         );
//         const data = await response.json();
//         setServices(data); // ✅ Store all services, no filtering!
//       } catch (error) {
//         console.error("Error fetching room services:", error);
//       }
//     };

//     fetchServices();
//   }, []);

//   // Fetch guest data on component mount
//   useEffect(() => {
//     const fetchGuestData = async () => {
//       const guestInfo = await getGuestData();
//       setGuestData(guestInfo);
//       setIsCorporateGuest(guestInfo?.is_corporate || false);
//     };

//     fetchGuestData();
//   }, []);

//   const handleBookClick = (service) => {
//     setSelectedService({
//       ...service,
//       category_name: service.name, // Since these are categories, their name IS the category name
//     });
//     setFormData({ ...formData, serviceName: service.name });
//     setIsModalOpen(true);
//   };

//   const confirmServiceBooking = async () => {
//     try {
//       if (!bookingId) {
//         throw new Error("Booking ID not found. Please try again.");
//       }

//       // Construct URL with query parameters
//       const url = new URL(
//         `${BQ_BASE_URL}/bq/api/create_amenity_service_billing`
//       );
//       url.searchParams.append("bookingid", bookingId);
//       url.searchParams.append("category_name", selectedService.category_name);

//       const response = await fetch(url.toString(), {
//         method: "POST",
//         headers: {
//           Accept: "application/json",
//         },
//         // No body needed since we're using query params
//       });

//       if (!response.ok) {
//         const errorData = await response.json();
//         throw new Error(errorData.detail || "Failed to confirm service");
//       }

//       const data = await response.json();

//       // Store billing details & selected service
//       localStorage.setItem("billingDetails", JSON.stringify(data));
//       localStorage.setItem("selectedService", JSON.stringify(selectedService));

//       toast.success("Service confirmed successfully");
//       closeConfirmationModal();
//       setShowPaymentModal(true);
//     } catch (error) {
//       toast.error(
//         error.message || "An error occurred while confirming the service."
//       );
//       console.error("Booking error:", error);
//     }
//   };

//   const closeConfirmationModal = () => {
//     setSelectedService(null);
//   };

//   useEffect(() => {
//     const loadRazorpayScript = () => {
//       const script = document.createElement("script");
//       script.src = "https://checkout.razorpay.com/v1/checkout.js";
//       script.async = true;
//       script.onload = () => setRazorpayLoaded(true);
//       script.onerror = () => {
//         toast.error("Error loading Razorpay script. Please try again.");
//       };
//       document.body.appendChild(script);
//     };

//     loadRazorpayScript();
//   }, []);

//   const handlePaymentOption = async (paymentMethod) => {
//     if (!razorpayLoaded) {
//       toast.error("Razorpay script is not loaded yet.");
//       return;
//     }

//     if (paymentMethod === "payNow") {
//       try {
//         // Retrieve selected service from localStorage
//         const selectedService = JSON.parse(
//           localStorage.getItem("selectedService")
//         );
//         if (!selectedService) {
//           toast.error("No service selected for payment.");
//           return;
//         }

//         // Retrieve billing details from localStorage
//         const billingDetails = JSON.parse(
//           localStorage.getItem("billingDetails")
//         );
//         if (!billingDetails || !billingDetails.billingid) {
//           toast.error("Billing details not found. Please try again.");
//           return;
//         }

//         const billingId = billingDetails.billingid;

//         const apiUrl = `${BQ_BASE_URL}/bq/api/razorpay/create_payment_order?billingid=${billingId}&bookingid=${bookingId}`;

//         const response = await fetch(apiUrl, {
//           method: "POST",
//           headers: {
//             "Content-Type": "application/json",
//             Accept: "application/json",
//           },
//         });

//         if (!response.ok) throw new Error("Failed to create payment order");

//         const orderData = await response.json();

//         if (orderData && orderData.razorpay_order_id) {
//           const options = {
//             key: REACT_APP_RAZORPAY_KEY,
//             amount: orderData.amount,
//             currency: "INR",
//             name: "Pagoda Hotel",
//             description: selectedService.name, // Show service name in payment
//             order_id: orderData.razorpay_order_id,
//             handler: async function (response) {
//               const paymentDetails = {
//                 razorpay_order_id: response.razorpay_order_id,
//                 razorpay_payment_id: response.razorpay_payment_id,
//                 razorpay_signature: response.razorpay_signature,
//               };

//               try {
//                 const verificationResponse = await fetch(
//                   `${BQ_BASE_URL}/bq/api/razorpay/verify_payment`,
//                   {
//                     method: "POST",
//                     headers: {
//                       "Content-Type": "application/json",
//                       Accept: "application/json",
//                     },
//                     body: JSON.stringify(paymentDetails),
//                   }
//                 );

//                 if (verificationResponse.ok) {
//                   toast.success("Payment successful and verified!");
//                   window.location.href = "/amenities-payment-succesful";
//                 } else {
//                   toast.error("Payment verification failed.");
//                 }
//               } catch (error) {
//                 console.error("Verification error:", error);
//                 toast.error("Error verifying payment. Please try again.");
//               }
//             },
//             prefill: {
//               name: `${
//                 bookingDetails.primaryBooking?.guest_name ||
//                 bookingDetails.primary_guest
//               }`,
//               email: bookingDetails.primaryBooking?.guest_email,
//               contact: bookingDetails.primaryBooking?.guest_phone?.toString(),
//             },
//             theme: {
//               color: "#3399cc",
//             },
//           };

//           const rzp = new window.Razorpay(options);
//           rzp.open();
//         }
//       } catch (error) {
//         console.error("Payment error:", error);
//         toast.error("Error processing payment. Please try again.");
//       }
//     } else if (paymentMethod === "payLater") {
//       toast.info("Payment deferred. Please pay at the front desk.");
//       localStorage.removeItem("selectedService");
//       localStorage.removeItem("billingDetails");
//       setShowPaymentModal(false);
//     } else if (paymentMethod === "billToCompany") {
//       toast.info(`Bill to company option selected. Charges will be billed to ${guestData?.company_name || 'your company'}.`);
//       localStorage.removeItem("selectedService");
//       localStorage.removeItem("billingDetails");
//       setShowPaymentModal(false);
//     }
//   };

//   const handlePreview = () => {
//     console.log("Preview data:", formData);
//     alert("Preview functionality would show a summary here");
//   };

//   const handleBook = () => {
//     console.log("Booking data:", formData);
//     alert("Booking submitted successfully!");
//     setIsModalOpen(false);
//   };

//   return (
//     <div className="min-h-screen bg-gray-50 py-8 px-4 sm:px-6 lg:px-8">
//       <Link
//         to="/reservation/options/booking/welcome"
//         className="absolute top-12 left-8 text-black hover:text-gray-800 transition font-semibold text-2xl"
//       >
//         <IoMdArrowRoundBack size={32} />
//       </Link>

//       <div className="px-10 mt-10 mx-auto">
//         <div className="text-center mb-12">
//           <h1 className="text-3xl font-poppins font-bold text-gray-900 mb-2">
//             Amenity Services
//           </h1>
//           <h2 className="text-lg font-poppins font-semibold text-center mb-1 text-black">
//             From essential conveniences to premium comforts, our amenity
//             services let you customize your stay to perfectly suit your needs
//           </h2>
//           <hr className="border border-gray-300 w-1/4 mx-auto mb-4" />
//         </div>

//         <div className="grid grid-cols-1 font-poppins gap-3 sm:grid-cols-2 lg:grid-cols-3">
//           {services.map((service) => (
//             <ServiceCard
//               key={service.id}
//               service={service}
//               onBookClick={handleBookClick}
//             />
//           ))}
//         </div>
//       </div>

//       {isModalOpen && (
//         <BookingModal
//           formData={formData}
//           setFormData={setFormData}
//           selectedService={selectedService}
//           onClose={() => setIsModalOpen(false)}
//           onPreview={handlePreview}
//           onBook={handleBook}
//           confirmServiceBooking={confirmServiceBooking}
//         />
//       )}
//       {showPaymentModal && (
//         <div className="fixed inset-0 flex justify-center items-center bg-gray-800 bg-opacity-70 z-50">
//           <div className="bg-white p-6 rounded-lg w-4/12 text-center shadow-md transform transition-all duration-200">
//             <h3 className="text-2xl font-semibold mb-4 text-gray-900">
//               Complete Your Payment
//             </h3>
//             <p className="text-md text-gray-600 mb-6">
//               Your booking has been successfully confirmed! Please select a
//               payment method to proceed:
//             </p>

//             <div className="flex justify-center gap-4">
//               {/* Pay Later option for leisure guests */}
//               {!isCorporateGuest && (
//                 <button
//                   onClick={() => handlePaymentOption("payLater")}
//                   className="bg-black text-white px-10 py-2 rounded-md hover:text-gray-200 transition-all duration-200 font-semibold"
//                 >
//                   Pay Later
//                 </button>
//               )}

//               {/* Bill to Company option for corporate guests */}
//               {isCorporateGuest && (
//                 <button
//                   onClick={() => handlePaymentOption("billToCompany")}
//                   className="bg-black text-white px-10 py-2 rounded-md hover:text-gray-200 transition-all duration-200 font-semibold"
//                 >
//                   Bill to Company
//                 </button>
//               )}

//               {/* Pay Now option for all guests */}
//               <button
//                 onClick={() => handlePaymentOption("payNow")}
//                 className="bg-green-600 text-white px-10 py-2 rounded-md hover:text-black transition-all duration-200 font-semibold"
//               >
//                 Pay Now
//               </button>
//             </div>
//           </div>
//         </div>
//       )}
//       <ToastContainer />
//     </div>
//   );
// };

// export default AmenityServices;
